import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { TrackerExtraInfo, TrackerPosition } from '@diritto/common/tracker/tracking-event.constant';
import { accurateItems, flatItems } from '@diritto/common/utils/array';
import { ReplyRelayDto } from '@diritto/common/relay/dtos/episode-reply-relay.dto';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { SectionDto } from '@diritto/common/home/dtos/section.dtos';
import { EpisodeReplyProvider } from 'contexts/EpisodeReplyProvider';
import { SectionReply } from 'app-components/reply/SectionReply';
import { useGetRelayRepliesInfiniteQuery } from 'queries/reply/useGetRelayRepliesInfiniteQuery';
import { InfiniteScrollWrapper } from 'ui-components/common/InfiniteScrollWrapper';
import { useHomeSettingState } from 'contexts/HomeSettingProvider';
import { useBlockProfiles } from '@/state-hooks/accounts/useBlockProfiles';

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.SectionDetail,
};

export const SectionReplyList = ({ section }: { section: SectionDto }) => {
  const { categoryIds } = useHomeSettingState();
  const blockedProfileIds = useBlockProfiles();
  const essentialCategoryIds = useMemo(
    () => (section.isEssentialVisible ? [] : categoryIds),
    [section.isEssentialVisible, categoryIds],
  );

  const { data, hasNextPage, fetchNextPage } = useGetRelayRepliesInfiniteQuery(essentialCategoryIds);

  const { replies, profileLites, contents, episodes } = useMemo(
    () => ({
      replies: flatItems<ReplyRelayDto>('replies', data?.pages) ?? [],
      contents: accurateItems<ContentPublicRelayDto>('contents', data?.pages) ?? {},
      episodes: accurateItems<EpisodePublicRelayDto>('episodes', data?.pages) ?? {},
      profileLites: accurateItems<ProfileLitePublicRelayDto>('profileLites', data?.pages) ?? {},
    }),
    [data?.pages],
  );

  return (
    <EpisodeReplyProvider contentId={0}>
      <StyledInfiniteScrollWrapper hasNext={hasNextPage} loadMore={fetchNextPage}>
        {replies.map(reply => {
          if (reply.isDeleted || (reply.profileId && blockedProfileIds.has(reply.profileId))) {
            return null;
          }

          return (
            <StyledSectionReply
              key={reply.id}
              reply={reply}
              contents={contents}
              episodes={episodes}
              profileLites={profileLites}
              trackerExtraInfo={trackerExtraInfo}
              descriptionLineCount={2}
            />
          );
        })}
      </StyledInfiniteScrollWrapper>
    </EpisodeReplyProvider>
  );
};

const StyledInfiniteScrollWrapper = styled(InfiniteScrollWrapper)`
  padding: 16px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StyledSectionReply = styled(SectionReply)`
  height: 180px;
`;
